import { useCallback, useMemo } from 'react';
import { Event } from 'types/Event';
import { useTranslation } from 'react-i18next';
import { UserIcon } from '@heroicons/react/24/outline';
import Icon from 'components/Icon/Icon';
import dayjs from 'dayjs';
import { TbList } from 'react-icons/tb';
import { MeetingStatusSetByMediator } from 'types/Meeting';
import Button from 'common/components/Button/Button';
import {
  getRRuleExdates,
  getRRuleFrequency,
  getRRuleFrom,
  getRRuleInterval,
  getRRuleUntil,
  getRecurrenceDescription,
} from 'common/utils/rrule';
import Status from 'common/components/Status/Status';
import useRouter from 'common/hooks/use-router';
import DocumentSection from './DocumentSection';
import EventStatusBadge from './EventStatusBadge';

interface EventDetailsProps {
  event: Event | null;
  onDocumentAdded?: (documentId: number) => Promise<void>;
  onDocumentDeleted?: (documentId: number) => Promise<void>;
  onChangeMeetingStatus?: (status: MeetingStatusSetByMediator) => Promise<void>;
}

function EventDetails({
  event,
  onDocumentAdded,
  onDocumentDeleted,
  onChangeMeetingStatus,
}: EventDetailsProps) {
  const { t } = useTranslation();

  const isMeeting = event?.eventType === 'meeting';

  const { navigate } = useRouter();

  const navigateToProfile = (userId?: string | number) => {
    if (!userId) return;
    navigate(`/students/${userId}`);
  };

  // Préparer les documents pour l'affichage
  const applicantDocuments = useMemo(
    () => event?.meeting?.documents?.map((document) => ({ ...document, type: 'applicant' })) ?? [],
    [event?.meeting?.documents]
  );

  const mediatorDocuments = useMemo(
    () =>
      event?.meeting?.mediatorDocuments?.map((document) => ({ ...document, type: 'mediator' })) ??
      [],
    [event?.meeting?.mediatorDocuments]
  );

  const documents = [...applicantDocuments, ...mediatorDocuments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const handleAccept = useCallback(async () => {
    await onChangeMeetingStatus?.('accepted');
  }, [onChangeMeetingStatus]);

  const handleRefuse = useCallback(async () => {
    await onChangeMeetingStatus?.('denied');
  }, [onChangeMeetingStatus]);

  const handleRescheduleByMediator = useCallback(async () => {
    await onChangeMeetingStatus?.('rescheduled_by_mediator');
  }, [onChangeMeetingStatus]);

  const handleCancel = useCallback(async () => {
    await onChangeMeetingStatus?.('cancelled');
  }, [onChangeMeetingStatus]);

  if (!event) return null;

  const excludedDates = event.rrule ? getRRuleExdates(event.rrule) : [];

  return (
    <div className="w-full">
      <div className="flex flex-row items-center w-full">
        <h2 className="text-xl text-center font-semibold w-full">
          <small className="capitalize">{dayjs(event.start).format('dddd DD MMMM')}</small>
          <br />
          {dayjs(event.start).format('HH:mm')} - {dayjs(event.end).format('HH:mm')}
        </h2>
      </div>

      {/* Status */}
      <div className="w-full flex flex-row items-center justify-center mt-4">
        <EventStatusBadge event={event} outlined={false} />
      </div>

      {isMeeting &&
        event.meeting &&
        ['pending', 'rescheduled_by_applicant'].includes(event.meeting?.status) && (
          <div className="p-4 mt-4 flex flex-row justify-center gap-2 ">
            <Button
              type="valid"
              onClick={handleAccept}
              icon="CheckIcon"
              title={t('actions.acceptMeeting')}
            />
            <Button
              type="error"
              onClick={handleRefuse}
              icon="XMarkIcon"
              title={t('actions.refuseMeeting')}
            />
          </div>
        )}

      <div className="white-box mt-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-row gap-4 items-center justify-center">
            <p className="flex-1 text-center">
              <strong>{event.title}</strong>
            </p>
          </div>
          {event.description && (
            <div className="flex flex-row items-center pl-7 -mt-2">
              <p className="flex-1">{event.description}</p>
            </div>
          )}
          {event.eventType === 'availability' && event.mediatorOnEstablishment && (
            <div className="flex flex-row gap-4 items-center">
              <Icon name="EstablishmentIcon" className="w-6 h-6" />
              <p className="flex-1">{event.mediatorOnEstablishment.name}</p>
            </div>
          )}

          {/* Informations de récurrence */}
          {event?.isRecurringInstance && event.rrule && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-4 items-center">
                <Icon name="IoRefreshCircle" className="w-6 h-6 flex-shrink-0" />
                <p className="flex-1" style={{ whiteSpace: 'pre' }}>
                  {getRecurrenceDescription({
                    frequency: getRRuleFrequency(event.rrule),
                    interval: getRRuleInterval(event.rrule),
                    from: getRRuleFrom(event.rrule),
                    until: getRRuleUntil(event.rrule),
                    capitalize: true,
                  })}
                  <br />
                </p>
              </div>

              {/* Dates exclues */}
              {excludedDates.length > 0 && (
                <div className="flex flex-row gap-4 items-start pl-10">
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">
                      {t(excludedDates.length > 1 ? 'agenda.exceptDates' : 'agenda.exceptDate')}
                    </span>{' '}
                    {excludedDates.map((date) => dayjs(date).format('DD MMM')).join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isMeeting &&
        (event.meeting ? (
          <>
            <div className="white-box mt-4">
              <div className="flex flex-col gap-4">
                <div
                  className="flex flex-col w-full border border-gray-200 hover:border-gray-300 hover:shadow-sm py-1 px-2 -mx-2 -mt-2 rounded-xl m-0 cursor-pointer"
                  onClick={() => {
                    navigateToProfile(event.meeting?.applicantId);
                  }}
                >
                  <div className="flex flex-row justify-between items-center w-full gap-4">
                    <UserIcon className="w-6 h-6" />
                    <p className="flex-1">
                      <strong>{event.meeting?.applicantProfileName}</strong>
                    </p>
                    <Status
                      status={
                        event.meeting?.applicantProfile?.isValidStudent
                          ? 'student-validated'
                          : 'student-not-validated'
                      }
                      small
                      useTooltip
                    />
                  </div>
                  <div className="flex flex-row justify-between items-center w-full gap-2 ml-5">
                    <Icon name="EstablishmentIcon" className="w-6 h-4" />
                    <p className="flex-1">{event.meeting?.applicantProfile?.establishment?.name}</p>
                  </div>
                  <div className="flex flex-row justify-between items-center w-full gap-2 ml-5">
                    <Icon name="Training" className="w-6 h-4" />
                    <p className="flex-1">{event.meeting?.applicantProfile?.training}</p>
                  </div>
                </div>
                {event.meeting?.applicant?.email && (
                  <div className="flex flex-row gap-4 items-center">
                    <Icon name="EnvelopeIcon" className="w-6 h-6" />
                    <p className="flex-1">{event.meeting?.applicant?.email}</p>
                  </div>
                )}
                {event.meeting?.applicantProfile?.phone && (
                  <div className="flex flex-row gap-4 items-center">
                    <Icon name="PhoneIcon" className="w-6 h-6" />
                    <p className="flex-1">{event.meeting?.applicantProfile?.phone}</p>
                  </div>
                )}
                {event.meeting?.modality && (
                  <div className="flex flex-row gap-4 items-center">
                    <Icon name="MapPinIcon" className="w-6 h-6" />
                    <p className="flex-1">
                      <strong>
                        {event.meeting?.modality === 'presence' ? 'En personne' : 'Au téléphone'}
                      </strong>
                    </p>
                  </div>
                )}
                {event.meeting?.reason?.name && (
                  <div className="flex flex-row gap-4 items-center">
                    <TbList className="w-5 h-5 flex-shrink-0" />
                    <p className="flex-1">{event.meeting?.reason?.name}</p>
                  </div>
                )}
                {event.meeting?.reason?.name === 'Autre' && event.meeting?.otherReason && (
                  <div className="flex flex-row items-center pl-7 -mt-2">
                    <p className="flex-1">{event.meeting?.otherReason}</p>
                  </div>
                )}
              </div>
            </div>

            <DocumentSection
              documents={documents}
              meetingId={event?.meeting?.id}
              applicantName={event?.meeting?.applicantProfileName}
              onDocumentAdded={onDocumentAdded}
              onDocumentDeleted={onDocumentDeleted}
              canEdit
            />

            {event.description && (
              <div className="white-box mt-4">
                <div className="flex flex-row gap-4 items-center mb-4">
                  <Icon name="PencilIcon" className="w-6 h-6" />
                  <p className="flex-1 font-bold">Notes</p>
                </div>
                <div className="flex flex-col gap-4">
                  <p>{event.description}</p>
                </div>
              </div>
            )}

            {[
              // for all active states
              'pending',
              'accepted',
              'rescheduled_by_applicant',
              'rescheduled_by_mediator',
            ].includes(event.meeting.status) && (
              <div className="p-4 mt-8 flex flex-row justify-center gap-2">
                <Button
                  type="error-line"
                  onClick={handleCancel}
                  icon="TrashIcon"
                  title={t('actions.cancel')}
                  confirm
                  confirmMessage={t('actions.confirmDelete')}
                />
                {event.meeting.status === 'accepted' && (
                  <Button
                    type="error-line"
                    onClick={handleRescheduleByMediator}
                    icon="RescheduleIcon"
                    title={t('actions.reschedule')}
                    confirm
                    confirmMessage={t('actions.confirmReschedule')}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <div className="white-box mt-4 flex flex-row w-full">
            <p className="flex-1">
              <strong>{t('error.noMeeting')}</strong>
            </p>
          </div>
        ))}
    </div>
  );
}

export default EventDetails;
